import { type ReactNode, createContext, useCallback, useMemo, useState } from "react";

import { Toast, type ToastAction, type ToastItem, type ToastVariant } from "./Toast";

export interface ToastOptions {
  action?: ToastAction;
}

export interface ToastContextValue {
  success: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
  info: (message: string, options?: ToastOptions) => void;
  warning: (message: string, options?: ToastOptions) => void;
}

export const ToastContext = createContext<ToastContextValue | null>(null);

interface ToastProviderProps {
  children: ReactNode;
}

const autoDismissMs = 5000;

export function ToastProvider({ children }: ToastProviderProps): JSX.Element {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const remove = useCallback((id: string): void => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);
  const add = useCallback(
    (variant: ToastVariant, message: string, options?: ToastOptions): void => {
      const id = crypto.randomUUID();
      const toast = buildToast(id, variant, message, options);
      setToasts((current) => [...current, toast]);
      window.setTimeout(() => remove(id), autoDismissMs);
    },
    [remove],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      success: (message, options) => add("success", message, options),
      error: (message, options) => add("error", message, options),
      info: (message, options) => add("info", message, options),
      warning: (message, options) => add("warning", message, options),
    }),
    [add],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed right-0 top-0 z-[60] flex w-full flex-col gap-2 p-3 sm:right-4 sm:top-4 sm:w-96 sm:p-0">
        {toasts.map((toast) => (
          <Toast key={toast.id} toast={toast} onClose={remove} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function buildToast(
  id: string,
  variant: ToastVariant,
  message: string,
  options?: ToastOptions,
): ToastItem {
  if (options?.action === undefined) return { id, variant, message };
  return { id, variant, message, action: options.action };
}
